import React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import { isLinkableAssetId } from '../../services/informationSystemsApi'
import BulkAssignAssetsModal, { type BulkAssignAssetRow } from './BulkAssignAssetsModal'
import type { InformationSystemDetail } from '../../services/informationSystemsApi'
import isStyles from '../../styles/components/assets/AssetsInformationSystems.module.scss'

interface Props {
  system: InformationSystemDetail
  inventoryAssets: BulkAssignAssetRow[]
  onLinkAsset: (assetId: string) => Promise<void>
  onUnlinkAsset: (assetId: string) => Promise<void>
  onBulkLink: (assetIds: string[]) => Promise<{
    linked?: string[]
    skipped?: string[]
    errors?: Array<{ assetId: string; message: string }>
  }>
  onBulkUnlink: (assetIds: string[]) => Promise<{
    unlinked?: string[]
    errors?: Array<{ assetId: string; message: string }>
  }>
  linking?: boolean
  compact?: boolean
}

export default function InformationSystemAssetsSection({
  system,
  inventoryAssets,
  onLinkAsset,
  onUnlinkAsset,
  onBulkLink,
  onBulkUnlink,
  linking,
  compact,
}: Props) {
  const { t } = useI18n()
  const [bulkMode, setBulkMode] = React.useState<'link' | 'unlink' | null>(null)
  const [selectedAssetId, setSelectedAssetId] = React.useState('')
  const linkedIds = new Set((system.assetLinks || []).map((l) => String(l.assetId)))
  const linkable = inventoryAssets.filter((a) => isLinkableAssetId(a.id) && !linkedIds.has(String(a.id)))
  const linkableInventory = inventoryAssets.filter((a) => isLinkableAssetId(a.id))

  return (
    <section className={isStyles.contextSectionBody}>
      <div className={isStyles.assetsSectionHeader}>
        <h4 className={isStyles.assetsSectionTitle}>
          {t('assets.passport.assets', 'Assets')} ({system.linkedAssetsCount ?? system.assetLinks?.length ?? 0})
        </h4>
        <div className={isStyles.assetsSectionActions}>
          <Button type="button" size="small" color="outline" onClick={() => setBulkMode('link')}>
            {t('assets.bulk.assignTitle', 'Assign assets')}
          </Button>
          <Button type="button" size="small" color="outline" onClick={() => setBulkMode('unlink')}>
            {t('assets.bulk.unlinkTitle', 'Unlink assets')}
          </Button>
        </div>
      </div>

      {(system.assetLinks || []).length === 0 ? (
        <p className={isStyles.emptyHint}>{t('assets.informationSystems.noLinkedAssets', 'No assets linked yet.')}</p>
      ) : (
        <div className={isStyles.assetsLinkTableWrap}>
          <table className={isStyles.assetsLinkTable}>
            <thead>
              <tr>
                <th>{t('assets.inventory.table.name', 'Name')}</th>
                <th>{t('assets.inventory.table.type', 'Type')}</th>
                <th>{t('assets.inventory.table.environment', 'Environment')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(system.assetLinks || []).map((link) => (
                <tr key={link.id}>
                  <td>{link.asset?.name || `#${link.assetId}`}</td>
                  <td>{link.asset?.type || '—'}</td>
                  <td>{link.asset?.environment || '—'}</td>
                  <td className={isStyles.assetsLinkTableActions}>
                    <Link to={`/assets/${link.assetId}`}>{t('assets.informationSystems.technical', 'Technical record')}</Link>
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      disabled={linking}
                      onClick={() => onUnlinkAsset(String(link.assetId))}
                    >
                      {t('assets.informationSystems.unlink', 'Unlink')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {linkable.length > 0 ? (
        <div className={isStyles.assetsQuickLinkRow}>
          <select
            className={isStyles.assetsQuickLinkSelect}
            value={selectedAssetId}
            onChange={(e) => setSelectedAssetId(e.target.value)}
          >
            <option value="">{t('assets.informationSystems.selectAsset', 'Select asset…')}</option>
            {linkable.map((a) => (
              <option key={a.id} value={String(a.id)}>
                {a.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="small"
            disabled={!selectedAssetId || linking}
            onClick={() => onLinkAsset(selectedAssetId).then(() => setSelectedAssetId(''))}
          >
            {t('assets.informationSystems.linkAsset', 'Link asset')}
          </Button>
        </div>
      ) : linkableInventory.length === 0 ? (
        <p className={isStyles.emptyHint}>
          {t(
            'assets.informationSystems.noLinkableInventory',
            'No inventory assets available to link. Add assets in Inventory first (scan-only assets cannot be linked).'
          )}
        </p>
      ) : null}

      <BulkAssignAssetsModal
        open={bulkMode != null}
        mode={bulkMode || 'link'}
        assets={linkableInventory}
        linkedAssetIds={linkedIds}
        onClose={() => setBulkMode(null)}
        onConfirm={async (assetIds) => {
          if (bulkMode === 'link') return onBulkLink(assetIds)
          return onBulkUnlink(assetIds)
        }}
      />
    </section>
  )
}
